import strings from "global/constants/StringConstants";
import { FileField } from "models/interfaces";

/**
 * Helpers for AI Auto-place. The server already drops the fields a PDF's own
 * form fields give the editor; this is the safety net for anything that gets
 * through, and for a second Auto-place over fields placed by the first.
 */

/** A field's box on its page, in percent of the page from the top-left. */
export interface PercentBox {
  page: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Page sizes in PDF points, keyed by 0-based page, as `file.dimensions`. */
export type PageDimensions = Record<
  number,
  { x?: number; y?: number } | undefined
>;

/** "The same field" (the AI's own rule): overlap of at least this share of either box. */
const SAME_FIELD_MIN_OVERLAP = 0.3;

const percentOr = (
  percent: number | undefined,
  points: number | undefined,
  pageSize: number | undefined,
): number | undefined => {
  if (percent !== undefined && percent !== null && percent !== 0) {
    return percent;
  }
  if (pageSize && points !== undefined && points !== null) {
    return (points / pageSize) * 100;
  }
  return undefined;
};

/**
 * Where a field sits, in percent. A placed field carries percent coordinates;
 * a PDF's own field fresh from upload still carries PDF points (x/y/w/h), which
 * need the page size -- the same conversion the Assign Pre-filled Fields modal
 * makes. `null` when neither is known.
 */
export const fieldPercentBox = (
  field: Partial<FileField>,
  dimensions?: PageDimensions,
): PercentBox | null => {
  const page = field.page ?? 0;
  const dims = dimensions?.[page];
  const x = percentOr(field.fieldPageX, field.x, dims?.x);
  const y = percentOr(field.fieldPageY, field.y, dims?.y);
  const w = percentOr(field.fieldPageWidth, field.w, dims?.x);
  const h = percentOr(field.fieldPageHeight, field.h, dims?.y);
  if (x === undefined || y === undefined || w === undefined || h === undefined) {
    return null;
  }
  return { page, x0: x, y0: y, x1: x + w, y1: y + h };
};

const centreInside = (box: PercentBox, other: PercentBox) => {
  const cx = (box.x0 + box.x1) / 2;
  const cy = (box.y0 + box.y1) / 2;
  return cx >= other.x0 && cx <= other.x1 && cy >= other.y0 && cy <= other.y1;
};

/**
 * Same page, and the overlap covers at least 30% of either box, or the centre
 * of one lies inside the other.
 */
export const isSameField = (a: PercentBox, b: PercentBox): boolean => {
  if (a.page !== b.page) return false;
  const w = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
  const h = Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0);
  const intersection = w > 0 && h > 0 ? w * h : 0;
  const areaA = (a.x1 - a.x0) * (a.y1 - a.y0);
  const areaB = (b.x1 - b.x0) * (b.y1 - b.y0);
  if (
    (areaA > 0 && intersection >= SAME_FIELD_MIN_OVERLAP * areaA) ||
    (areaB > 0 && intersection >= SAME_FIELD_MIN_OVERLAP * areaB)
  ) {
    return true;
  }
  return centreInside(a, b) || centreInside(b, a);
};

/**
 * The detected fields that are not already on the document: not on top of an
 * existing field, nor of one detected before them in the same list. A field
 * whose position cannot be worked out is kept (and never hides another).
 */
export const withoutOverlappingFields = <T extends Partial<FileField>>(
  detected: T[],
  existing: Partial<FileField>[],
  dimensions?: PageDimensions,
): T[] => {
  const taken: PercentBox[] = existing
    .map((f) => fieldPercentBox(f, dimensions))
    .filter((b): b is PercentBox => b !== null);
  const kept: T[] = [];
  detected.forEach((field) => {
    const box = fieldPercentBox(field, dimensions);
    if (box === null) {
      kept.push(field);
      return;
    }
    if (taken.some((t) => isSameField(box, t))) return;
    taken.push(box);
    kept.push(field);
  });
  return kept;
};

/**
 * `count` field ids that no existing field holds, lowest first. Counting up
 * from the lowest free id alone would hand out ids existing fields already
 * hold (e.g. with ids 1 and 3, the second new field would get 3 again).
 */
export const allocateFieldIds = (
  existing: Partial<FileField>[],
  count: number,
): number[] => {
  const used = new Set(existing.map((f) => f.id));
  const ids: number[] = [];
  for (let id = 1; ids.length < count; id++) {
    if (!used.has(id)) ids.push(id);
  }
  return ids;
};

/**
 * The editor's own name for a type the server sends. The server names types
 * like its enum (`date_signed`, `company_name`); the editor's field palette,
 * icons and property panels use `strings.dateSigned` / `strings.companyName`.
 */
export const toEditorFieldType = (type?: string): string => {
  switch (type) {
    case "date_signed":
      return strings.dateSigned;
    case "company_name":
      return strings.companyName;
    default:
      return type ?? strings.text;
  }
};
